import { type ActiveElement, type ChartEvent } from "chart.js";
import { Bubble } from "react-chartjs-2";

import { useCSSVariable } from "@/utils/colorConverter";
import { BackgroundPlugin } from "@features/metrics-explorer/components/metrics/chart-plugins";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { useProductAnalyticsContext } from "../hooks";
import { filterDataByColumnAndOperation } from "../metricsData";
import { type MetricFilter } from "../types";

const lerp = (start: number, end: number, t: number) =>
  start + (end - start) * t;

const blendColors = (color1: string, color2: string, ratio: number) => {
  const hexToRgb = (hex: string) => {
    const bigint = parseInt(hex.slice(1), 16);
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
  };

  const [r1, g1, b1] = hexToRgb(color1);
  const [r2, g2, b2] = hexToRgb(color2);

  const r = Math.round(lerp(r1, r2, ratio));
  const g = Math.round(lerp(g1, g2, ratio));
  const b = Math.round(lerp(b1, b2, ratio));

  return `rgb(${r}, ${g}, ${b})`;
};

const getBlendedColor = (x: number) => {
  const minX = -25;
  const maxX = 125;
  const clampedX = Math.min(Math.max(x, minX), maxX);
  const t = (clampedX - minX) / (maxX - minX); // Normalize x to 0–1

  // Define your gradient stops
  const colorStart = "#C825A8"; // Green
  const colorMid = "#FFD247"; // Yellow
  const colorEnd = "#50BA58"; // Magenta

  if (t < 0.5) {
    // Blend between start and mid
    return blendColors(colorStart, colorMid, t * 2);
  } else {
    // Blend between mid and end
    return blendColors(colorMid, colorEnd, (t - 0.5) * 2);
  }
};

const getBlendedColorWithAlpha = (x: number, _y: number, alpha: number) => {
  const baseColor = getBlendedColor(x);
  const rgb = baseColor.match(/\d+/g);
  if (!rgb) {
    return `rgba(0,0,0,${alpha})`;
  }
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
};

export const transformGraphData = (
  tabGraphData: any,
  formatting: any,
  user: any,
  metricFilters: MetricFilter[],
) => {
  let filteredRows = tabGraphData;
  if (metricFilters.length !== 0) {
    filteredRows = metricFilters.reduce((inProgressFilteredRows, filter) => {
      return filterDataByColumnAndOperation(
        filter.value,
        inProgressFilteredRows,
        filter.operator,
        filter.id,
      );
    }, tabGraphData);
  }
  const highestSpend = Math.max(...tabGraphData.map((item: any) => item.spend));

  return filteredRows.map((item, index) => ({
    x: item.cacPercentage,
    y: item.roasPercentage,
    r:
      (item.spend / highestSpend) * 100 < 5
        ? 5
        : (item.spend / highestSpend) * 100 > 25
        ? 25
        : (item.spend / highestSpend) * 100,
    id: item.id,
    label: item.name,
    proportional_spend: formatting.formatCurrency(item.proportional_spend, {
      code: user.currencies.currentCurrency,
    }),
    color: getBlendedColor(item.cacPercentage || 0),
    backgroundColor: getBlendedColorWithAlpha(item.cacPercentage || 0, 0, 0.85),
    borderColor: getBlendedColorWithAlpha(item.cacPercentage || 0, 0, 1),
  }));
};

const customDashedLinePlugin = {
  id: "customDashedLines",
  beforeDraw: (chart: any) => {
    const {
      ctx,
      scales: { x, y },
      chartArea: { left, top, width, height },
    } = chart;

    ctx.save();
    const radius = 4;
    const r = Math.min(radius, width / 2, height / 2);

    ctx.beginPath();
    ctx.moveTo(left + r, top);
    ctx.lineTo(left + width - r, top);
    ctx.quadraticCurveTo(left + width, top, left + width, top + r);
    ctx.lineTo(left + width, top + height - r);
    ctx.quadraticCurveTo(
      left + width,
      top + height,
      left + width - r,
      top + height,
    );
    ctx.lineTo(left + r, top + height);
    ctx.quadraticCurveTo(left, top + height, left, top + height - r);
    ctx.lineTo(left, top + r);
    ctx.quadraticCurveTo(left, top, left + r, top);
    ctx.closePath();
    ctx.save();

    for (let value = x.min; value <= x.max; value += 25) {
      if (value <= -25 || value >= 125) {
        continue;
      }

      const xPos = x.getPixelForValue(value);
      ctx.beginPath();
      ctx.strokeStyle = value === 50 ? "#D1D4DB" : "#E5E5E5";
      ctx.lineWidth = 1;
      ctx.setLineDash(value !== 50 && value >= 0 && value <= 100 ? [5, 5] : []);
      ctx.moveTo(xPos, y.getPixelForValue(y.min));
      ctx.lineTo(xPos, y.getPixelForValue(y.max));
      ctx.stroke();
    }

    for (let value = y.min; value <= y.max; value += 25) {
      if (value <= -25 || value >= 125) {
        continue;
      }
      const yPos = y.getPixelForValue(value);
      ctx.beginPath();
      ctx.strokeStyle = value === 50 ? "#D1D4DB" : "#E5E5E5";
      ctx.lineWidth = 1;
      ctx.setLineDash(value !== 50 && value >= 0 && value <= 100 ? [5, 5] : []);
      ctx.moveTo(x.getPixelForValue(x.min), yPos);
      ctx.lineTo(x.getPixelForValue(x.max), yPos);
      ctx.stroke();
    }

    ctx.restore();
  },
};

const QuadrantBubbleChart = ({ datasets }: { datasets: any }) => {
  const {
    tabState,
    setSelectedCampaigns,
    setSelectedPlatforms,
    setSelectedAds,
    setSelectedProducts,
    tableState,
    selectedQuickSelect,
    setQuickSelect,
    setSelectedChartDots,
    chartSelected,
  } = useProductAnalyticsContext();

  const prefs = usePrefs();
  const isAnon = prefs.isDemoMode;

  const fontColor = useCSSVariable("--v2-text-primary");

  const data = {
    datasets: [
      {
        data: datasets,
        backgroundColor: function (context) {
          return context?.raw?.backgroundColor || "rgba(128,128,128,0.8)";
        },
        borderColor: function (context) {
          return context?.raw?.borderColor || "rgba(128,128,128,1)";
        },
        borderWidth: 1,
        borderDash: [], // solid
        pointStyle: "circle",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "CAC Index",
          color: fontColor,
          font: {
            size: 12,
            weight: "600",
          },
        },
        min: -25,
        max: 125,
        ticks: {
          stepSize: 25,
          padding: -8,
          callback: () => "",
        },
        grid: {
          display: false,
          drawBorder: false,
        },
      },
      y: {
        title: {
          display: true,
          text: "ROAS Index",
          color: fontColor,
          font: {
            size: 12,
            weight: "600",
          },
        },
        min: -25,
        max: 125,
        ticks: {
          padding: 0,
          stepSize: 25,
          callback: () => "",
        },

        grid: {
          display: false,
          drawBorder: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        usePointStyle: true,
        callbacks: {
          label: function (context: {
            raw: { label: string; proportional_spend: string };
          }) {
            const label = generateAnonBase64Hash({
              value: context.raw.label || "",
              prefix: tabState.tabView,
              doAnon: isAnon,
            });
            const proportionalSpend = context.raw.proportional_spend || "";
            return [`${label}`, `Product Spend: ${proportionalSpend}`];
          },
        },
        labelPointStyle: {
          pointStyle: "circle",
          rotation: 0,
        },
      },
      title: {
        display: false,
      },
    },
    onClick: (event: ChartEvent, elements: ActiveElement[], chart: any) => {
      const setSelected =
        tabState.tabView === "product"
          ? setSelectedProducts
          : tabState.tabView === "platform"
          ? setSelectedPlatforms
          : tabState.tabView === "campaign"
          ? setSelectedCampaigns
          : setSelectedAds;

      const tableStateItems =
        tabState.tabView === "product"
          ? tableState.selectedProducts
          : tabState.tabView === "platform"
          ? tableState.selectedPlatforms
          : tabState.tabView === "campaign"
          ? tableState.selectedCampaigns
          : tableState.selectedAds;
      if (selectedQuickSelect?.quickSelect?.length > 0) {
        setQuickSelect("");
        const datasetIndex = elements[0].datasetIndex;
        const index = elements[0].index;
        const clickedBubble = data.datasets[datasetIndex].data[index];
        setSelected([clickedBubble.id]);
        setSelectedChartDots([clickedBubble.id]);
        return;
      }
      if (elements.length > 0) {
        const datasetIndex = elements[0].datasetIndex;
        const index = elements[0].index;

        const clickedBubble = data.datasets[datasetIndex].data[index];
        if (tableStateItems.includes(clickedBubble.id)) {
          setSelected(
            tableStateItems.filter(
              (productId) => productId !== clickedBubble.id,
            ),
          );
          setSelectedChartDots(
            tableStateItems.filter(
              (productId) => productId !== clickedBubble.id,
            ),
          );
        } else {
          setSelected([...tableStateItems, clickedBubble.id]);
          setSelectedChartDots([...chartSelected, clickedBubble.id]);
        }
      } else {
        const xValue = chart.scales.x.getValueForPixel(event.x);
        const yValue = chart.scales.y.getValueForPixel(event.y);

        let quadrant;
        if (xValue < 50 && yValue > 50) {
          quadrant = "Top-Left";
        } else if (xValue > 50 && yValue > 50) {
          quadrant = "Top-Right";
        } else if (xValue < 50 && yValue < 50) {
          quadrant = "Bottom-Left";
        } else if (xValue > 50 && yValue < 50) {
          quadrant = "Bottom-Right";
        }

        if (quadrant) {
          const filteredData = datasets.filter(
            (item: { x: number; y: number }) => {
              if (quadrant === "Top-Left") {
                return item.x < 50 && item.y > 50;
              } else if (quadrant === "Top-Right") {
                return item.x > 50 && item.y > 50;
              } else if (quadrant === "Bottom-Left") {
                return item.x < 50 && item.y < 50;
              } else if (quadrant === "Bottom-Right") {
                return item.x > 50 && item.y < 50;
              }
              return false;
            },
          );

          if (filteredData.length === 0) {
            setSelected([]);
            setSelectedChartDots([]);
            return;
          }

          const newSelectedProducts = filteredData.reduce(
            (acc: any[], item: { id: string }) => {
              if (tableStateItems.includes(item.id)) {
                return acc.filter((productId) => productId !== item.id);
              } else {
                return [...acc, item.id];
              }
            },
            tableStateItems,
          );

          setSelected(newSelectedProducts);
          setSelectedChartDots(newSelectedProducts);
        }
      }
    },
  };

  return (
    <Bubble
      data={data}
      options={options}
      plugins={[customDashedLinePlugin, BackgroundPlugin]}
    />
  );
};

export default QuadrantBubbleChart;
